/**
 * SA per-request cost drill-down — read-only table.
 *
 * Wired to GET /admin/usage/rows?request_id=…. Enter a request_id and see each
 * AI action that ran under it (eval / rank_one / resume_format / …) with its
 * own token + cost breakdown. No charts. Read-only.
 *
 * `cost_usd_estimate: null` → unknown model: render "—", never $0.
 *
 * Scope: filtered by request_id ONLY. No per-candidate filter — `usage_ledger`
 * has no `candidate_id` column (candidate-level would need new instrumentation).
 *
 * SA cross-tenant surface; the endpoint is gated by
 * `current_super_admin_not_impersonating`. Tenant-zone names are NOT shown here
 * (only tenant_id) — this is an operator cost tool, not a customer surface.
 */
import { useState } from "react";
import { useSaUsageRows } from "@/hooks/queries/admin/useSaDashboard";
import type { UsageRow } from "@/lib/saDashboardApi";
import { OperatorPanel } from "./OperatorPanel";

/** Format cost; null (unknown model) → "—", never $0. */
function fmtCost(c: number | null): string {
  if (c === null) return "—";
  return `$${c.toFixed(4)}`;
}

/** Compact token count: 41200 → "41.2k". */
function fmtTokens(n: number): string {
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function totalCost(rows: UsageRow[]): number | null {
  // Sum only known costs; if every row is unknown, return null (no $0 fib).
  let any = false;
  let sum = 0;
  for (const r of rows) {
    if (r.cost_usd_estimate !== null) {
      any = true;
      sum += r.cost_usd_estimate;
    }
  }
  return any ? sum : null;
}

function Row({ row }: { row: UsageRow }) {
  return (
    <tr className="border-b border-border-default last:border-0">
      <td className="py-1.5 pr-3 text-xs font-medium text-text-primary">
        {row.action}
      </td>
      <td className="py-1.5 pr-3 font-mono text-[11px] text-text-tertiary">
        {row.model}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-[11px] tabular-nums text-text-secondary">
        {fmtTokens(row.input_tokens)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-[11px] tabular-nums text-text-secondary">
        {fmtTokens(row.output_tokens)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-[11px] tabular-nums text-text-tertiary">
        {fmtTokens(row.cached_read_tokens + row.cached_write_tokens)}
      </td>
      <td
        className={
          "py-1.5 pr-3 text-right font-mono text-[11px] tabular-nums font-semibold " +
          (row.cost_usd_estimate === null
            ? "text-text-tertiary"
            : "text-text-primary")
        }
      >
        {fmtCost(row.cost_usd_estimate)}
      </td>
      <td
        className={
          "py-1.5 pr-3 text-xs " +
          (row.outcome === "success"
            ? "text-text-tertiary"
            : "text-brand-negative")
        }
      >
        {row.outcome}
      </td>
      <td className="py-1.5 text-[11px] text-text-tertiary">
        {fmtTime(row.created_at)}
      </td>
    </tr>
  );
}

export function UsageDrillTable() {
  const [input, setInput] = useState("");
  const [requestId, setRequestId] = useState("");
  const { data, isLoading, isError, isFetching } = useSaUsageRows(requestId);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setRequestId(input.trim());
  }

  const rows = data?.rows ?? [];
  const total = totalCost(rows);

  return (
    <OperatorPanel
      testId="usage-drill-panel"
      kicker="Per-request spend"
      title="Cost drill-down"
      headerRight={
        requestId && rows.length > 0 && total !== null ? (
          <span className="font-mono text-[11px] tabular-nums font-semibold text-text-primary">
            {fmtCost(total)} total
          </span>
        ) : undefined
      }
    >
      <div className="space-y-3">
        <form onSubmit={submit} className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste a request_id…"
            aria-label="request_id"
            className="min-w-0 flex-1 rounded-md border border-border-default bg-surface-canvas px-2.5 py-1.5 font-mono text-[11px] text-text-primary outline-none placeholder:font-sans placeholder:text-text-tertiary focus:border-accent-secondary"
          />
          <button
            type="submit"
            disabled={input.trim().length === 0}
            className="rounded-md border border-border-default px-3 py-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-text-secondary hover:border-accent-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Look up
          </button>
        </form>

        {!requestId && (
          <p className="text-xs text-text-tertiary">
            Enter a request_id to break down its AI-action spend (eval, rank_one,
            resume_format, …).
          </p>
        )}

        {requestId && isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-7 animate-pulse rounded bg-surface-canvas"
              />
            ))}
          </div>
        )}

        {requestId && isError && (
          <p className="text-xs text-brand-negative">
            Couldn't load usage rows for that request.
          </p>
        )}

        {requestId && !isLoading && !isError && rows.length === 0 && (
          <p
            data-testid="usage-drill-empty"
            className="text-xs text-text-tertiary"
          >
            No usage rows for that request_id.
          </p>
        )}

        {requestId && rows.length > 0 && (
          <div className="max-h-[320px] overflow-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-surface-card">
                <tr className="border-b border-border-strong text-left">
                  <th className="pb-1.5 pr-3 text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Action
                  </th>
                  <th className="pb-1.5 pr-3 text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Model
                  </th>
                  <th className="pb-1.5 pr-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    In
                  </th>
                  <th className="pb-1.5 pr-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Out
                  </th>
                  <th className="pb-1.5 pr-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Cached
                  </th>
                  <th className="pb-1.5 pr-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Cost
                  </th>
                  <th className="pb-1.5 pr-3 text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    Outcome
                  </th>
                  <th className="pb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <Row key={`${r.action}-${r.created_at}-${i}`} row={r} />
                ))}
              </tbody>
            </table>
            {isFetching && (
              <p className="mt-2 text-[10px] text-text-tertiary">refreshing…</p>
            )}
          </div>
        )}
      </div>
    </OperatorPanel>
  );
}
